import { createSubAnchorResolver } from './subanchors';

/**
 * Known GRS subanchor values for the `wacAjConfSubAnchor` (Confluence BPS v2)
 * use case.
 *
 * The `subAnchor` forwarded to the recommendation query must be one of these
 * values. Anything that is not a known subanchor (case-insensitive) is mapped
 * to {@link CONFLUENCE_DEFAULT_SUB_ANCHOR} so GRS always receives a recognised
 * value.
 *
 * These 39 values are the source of truth for the Confluence `conf-bps-v2`
 * `anchorKeywordMapping` pool — one per `anchorPermutation` bucket. Keep this
 * list in sync with the Contentful content; a value that is absent here is
 * silently downgraded to the catch-all bucket.
 *
 * Deliberately separate from the JSM list in `./subanchors` — the two
 * vocabularies do not overlap, and sending a JSM subanchor on the Confluence
 * use case (or vice versa) yields no usable recommendation.
 */
export const confluenceSubAnchors = [
	'business plan template',
	'collaboration software',
	'confluence alternatives',
	'confluence as a solution',
	'confluence cloud',
	'confluence connections',
	'confluence demo',
	'confluence features',
	'confluence for customer service teams',
	'confluence for design teams',
	'confluence for legal teams',
	'confluence for personal use',
	'confluence help',
	'confluence license',
	'confluence online',
	'confluence os',
	'confluence pricing',
	'confluence program',
	'confluence reviews',
	'confluence server',
	'confluence wiki',
	'core terms',
	'create an account',
	'default (catch all)',
	'documentation software',
	'download confluence',
	'get confluence',
	'how to use confluence',
	'install confluence',
	'knowledge base',
	'login to confluence',
	'official site',
	'project tracking',
	'signup for confluence for free',
	'spreadsheet',
	'start a confluence trial',
	'website creation',
	'what is confluence',
	'whiteboard features',
] as const;

/**
 * Fallback subanchor used when the incoming `subAnchor` is unknown or absent on
 * the Confluence use case. Matches the `default (catch all)` bucket exactly as
 * spelled in Contentful.
 */
export const CONFLUENCE_DEFAULT_SUB_ANCHOR = 'default (catch all)';

/**
 * Resolves a raw `subAnchor` to a known Confluence subanchor value.
 *
 * Returns the original value (preserving its casing) when it matches a known
 * subanchor case-insensitively; otherwise — including for empty/undefined
 * input — returns {@link CONFLUENCE_DEFAULT_SUB_ANCHOR}.
 */
export const resolveConfluenceSubAnchor = createSubAnchorResolver(
	confluenceSubAnchors,
	CONFLUENCE_DEFAULT_SUB_ANCHOR,
);
